import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Plus, Sparkles, CheckCircle } from 'lucide-react';
import { formatDate } from '../lib/utils';
import DictationInput from '../components/DictationInput';
import ToneSelector from '../components/ToneSelector';

export default function SessionNotesPage() {
  const { user, axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ client_id: '', subjective: '', objective: '', assessment: '', plan: '', session_type: 'individual' });
  const [aiForm, setAiForm] = useState({ client_name: '', session_type: 'individual', key_topics: '', observations: '' });
  const [showAi, setShowAi] = useState(false);
  const api = axiosAuth();
  const canCreate = ['peer_specialist', 'coordinator', 'admin'].includes(user.role);
  const canApprove = ['coordinator', 'admin'].includes(user.role);
  const filteredNotes = statusParam ? notes.filter(n => n.status === statusParam) : notes;
  const hasFilter = Boolean(statusParam);

  const load = () => {
    api.get('/session-notes').then(r => setNotes(r.data)).catch(() => {});
    if (canCreate) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.client_id) return toast.error('Select a client');
    try {
      await api.post('/session-notes', form);
      toast.success('Session note created!');
      setOpen(false);
      setForm({ client_id: '', subjective: '', objective: '', assessment: '', plan: '', session_type: 'individual' });
      load();
    } catch { toast.error('Failed to create note'); }
  };

  const handleAiGenerate = async () => {
    if (!aiForm.client_name || !aiForm.key_topics) return toast.error('Provide client name and key topics');
    setGenerating(true);
    try {
      const r = await api.post('/ai/generate-soap-note', aiForm);
      setForm({ ...form, subjective: r.data.subjective || '', objective: r.data.objective || '', assessment: r.data.assessment || '', plan: r.data.plan || '' });
      setShowAi(false);
      toast.success('AI-generated SOAP note ready for review!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/session-notes/${id}/approve`);
      toast.success('Note approved!');
      load();
    } catch { toast.error('Failed to approve'); }
  };

  return (
    <div className="space-y-6" data-testid="session-notes-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Session Notes</h1><p className="text-muted-foreground text-sm mt-1">SOAP format documentation</p></div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-session-note-button"><Plus size={16} /> New Note</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Create Session Note (SOAP)</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex gap-2">
                  <Button variant={!showAi ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setShowAi(false)}>Manual Entry</Button>
                  <Button variant={showAi ? "default" : "outline"} size="sm" className="rounded-full gap-1" onClick={() => setShowAi(true)} data-testid="ai-assist-button"><Sparkles size={14} /> AI Assist</Button>
                </div>

                {showAi ? (
                  <div className="space-y-3 bg-teal-50 rounded-xl p-4 border border-teal-100">
                    <p className="text-sm font-medium text-teal-800">AI-Powered SOAP Note Generator</p>
                    <div className="space-y-1.5"><Label className="text-sm">Client Name</Label><Input value={aiForm.client_name} onChange={e => setAiForm({...aiForm, client_name: e.target.value})} placeholder="Client's name" data-testid="ai-client-name" /></div>
                    <div className="space-y-1.5"><Label className="text-sm">Key Topics Discussed</Label>
                      <DictationInput value={aiForm.key_topics} onChange={(cleaned) => setAiForm(f => ({...f, key_topics: cleaned}))} rows={2} placeholder="What was discussed in the session?" data-testid="ai-key-topics" />
                    </div>
                    <div className="space-y-1.5"><Label className="text-sm">Observations</Label>
                      <DictationInput value={aiForm.observations} onChange={(cleaned) => setAiForm(f => ({...f, observations: cleaned}))} rows={2} placeholder="What did you observe?" data-testid="ai-observations" />
                    </div>
                    <Button onClick={handleAiGenerate} disabled={generating} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="generate-soap-button">
                      <Sparkles size={16} /> {generating ? 'Generating...' : 'Generate SOAP Note'}
                    </Button>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-sm">Client</Label>
                    <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                      <SelectTrigger data-testid="note-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-sm">Type</Label>
                    <Select value={form.session_type} onValueChange={v => setForm({...form, session_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="individual">Individual</SelectItem><SelectItem value="group">Group</SelectItem><SelectItem value="crisis">Crisis</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-sm">S - Subjective</Label>
                  <DictationInput value={form.subjective} onChange={(cleaned) => setForm(f => ({...f, subjective: cleaned}))} rows={2} placeholder="Client's reported experience..." promptContext={{ context: 'session_note' }} data-testid="soap-subjective" />
                </div>
                <div className="space-y-1.5"><Label className="text-sm">O - Objective</Label>
                  <DictationInput value={form.objective} onChange={(cleaned) => setForm(f => ({...f, objective: cleaned}))} rows={2} placeholder="Observable behaviors..." data-testid="soap-objective" />
                </div>
                <div className="space-y-1.5"><Label className="text-sm">A - Assessment</Label>
                  <DictationInput value={form.assessment} onChange={(cleaned) => setForm(f => ({...f, assessment: cleaned}))} rows={2} placeholder="Clinical assessment..." data-testid="soap-assessment" />
                </div>
                <div className="space-y-1.5"><Label className="text-sm">P - Plan</Label>
                  <DictationInput value={form.plan} onChange={(cleaned) => setForm(f => ({...f, plan: cleaned}))} rows={2} placeholder="Next steps and plan..." data-testid="soap-plan" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-session-note-submit">Save Session Note</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {hasFilter && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700" data-testid="session-notes-filter-summary">
          Showing: {statusParam} notes
        </div>
      )}

      <div className="space-y-3">
        {filteredNotes.length > 0 ? filteredNotes.map(n => (
          <Card key={n.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium capitalize">{n.session_type} Session Note</p>
                  <p className="text-xs text-muted-foreground">By {n.specialist_name} &middot; {formatDate(n.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={n.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'} variant="secondary">{n.status}</Badge>
                  {canApprove && n.status === 'draft' && (
                    <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs text-emerald-600" onClick={() => handleApprove(n.id)} data-testid={`approve-note-${n.id}`}>
                      <CheckCircle size={14} /> Approve
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {['subjective', 'objective', 'assessment', 'plan'].map(k => (
                  <div key={k} className="bg-muted/30 rounded-xl p-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{k.charAt(0)} - {k}</p>
                    <p className="text-sm">{n[k] || 'N/A'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><ClipboardList className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No session notes yet.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
